import { useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import { useToast } from 'hooks/use-toast';
import { useConfig } from 'hooks/use-config';
import { useUpdateAgentPersona, useUpdateWidget } from 'features/chat/hooks/use-agents';
import type { Agent } from 'features/chat/agent-chat-module/types/agent.service.type';
import { useGetPreSignedUrlForUpload } from 'lib/api/hooks/use-storage';
import { getFile } from 'services/storage-service';
import { cn } from 'lib/utils';

type AgentImageUploaderProps = {
  agent: Agent;
  className?: string;
  disabled?: boolean;
};

export const AgentImageUploader = ({ agent, className, disabled = false }: AgentImageUploaderProps) => {
  const { t } = useTranslation();
  const [image, setImage] = useState<string | null>(agent?.logo_url || null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { configurationSettings } = useConfig();

  const presignedUploadMutation = useGetPreSignedUrlForUpload();
  const projectKey = configurationSettings.blocksKey || '';

  const { mutateAsync: updateAgent } = useUpdateAgentPersona();
  const { mutateAsync: updateWidget } = useUpdateWidget();

  const uploadImage = async (file: File, fallbackDirectUrl?: string) => {
    try {
      setIsUploading(true);

      const presign = await presignedUploadMutation.mutateAsync({
        name: file.name,
        projectKey: projectKey,
        metaData: '',
        accessModifier: 'Public',
        configurationName: 'Default',
        parentDirectoryId: '',
        tags: '',
      });

      if (!presign.isSuccess || !presign.uploadUrl || !presign.fileId) {
        throw new Error(`Failed to get upload URL for ${file.name}`);
      }

      await uploadFileToStorage(presign.uploadUrl, file);

      const fileInfo = await getFile(presign.fileId, configurationSettings);

      if (!fileInfo.isSuccess || !fileInfo.url) {
        throw new Error(`Failed to resolve uploaded image URL for ${file.name}`);
      }

      const payload = {
        ...agent,
        logo_id: presign.fileId,
        logo_url: fileInfo.url,
        agent_id: agent.id,
        project_key: projectKey,
        agent_type: agent.agent_type || '',
        role: agent.role ?? '',
      };

      const res = await updateAgent(payload);
      updateWidget({
        agent_id: agent.id,
        project_key: projectKey,
        regen_widget_id: false,
        widget: { ...agent.widget, logo_id: presign.fileId, logo_url: fileInfo.url },
      });
      if (!res.is_success) {
        toast({
          title: 'Upload failed',
          description: typeof res.error === 'string' ? res.error : 'Could not update agent image.',
          variant: 'destructive',
        });
        return;
      }

      setImage(fileInfo.url);
      toast({
        variant: 'success',
        title: 'Image updated',
        description: 'Agent image changed successfully',
      });
    } catch (err) {
      if (fallbackDirectUrl) {
        try {
          const directPayload = {
            ...agent,
            logo_id: agent.logo_id || '',
            logo_url: fallbackDirectUrl,
            agent_id: agent.id,
            project_key: projectKey,
            agent_type: agent.agent_type || '',
            role: agent.role ?? '',
          };
          const res = await updateAgent(directPayload);
          updateWidget({
            agent_id: agent.id,
            project_key: projectKey,
            regen_widget_id: false,
            widget: { ...agent.widget, logo_url: fallbackDirectUrl },
          });
          if (res.is_success) {
            setImage(fallbackDirectUrl);
            toast({
              variant: 'success',
              title: 'Image updated',
              description: 'Agent image changed successfully',
            });
            return;
          }
        } catch (_fallbackErr) {
          // fall through to regular error toast
        }
      }

      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Something went wrong uploading image',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFileToStorage = async (url: string, file: File) => {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
        'x-ms-blob-type': 'BlockBlob',
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed for ${file.name}`);
    }

    return { uploadUrl: url.split('?')[0] };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Only image files are allowed',
        variant: 'destructive',
      });
      return;
    }

    const MAX_SIZE = 1 * 1024 * 1024; // 1MB
    if (file.size > MAX_SIZE) {
      toast({
        title: 'File too large',
        description: 'Image must be less than 1 MB',
        variant: 'destructive',
      });
      return;
    }

    setImage(URL.createObjectURL(file));
    uploadImage(file);
    e.target.value = '';
  };

  const agentInitial = agent.name?.charAt(0).toUpperCase() || 'A';
  const isBusy = isUploading;

  return (
    <div className={cn('flex flex-col items-center justify-center gap-5 lg:col-span-3 lg:justify-start', className)}>
      <div className="relative aspect-square h-40 w-40 overflow-hidden rounded-full">
        <Avatar className="h-40 w-40">
          {image ? <AvatarImage src={image} alt="Agent Profile" className="object-cover" /> : null}
          <AvatarFallback className="bg-muted text-3xl font-medium text-foreground">
            {agentInitial}
          </AvatarFallback>
        </Avatar>
        {isBusy && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/75">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        className="hidden"
        disabled={disabled || isBusy}
        onChange={handleFileChange}
      />

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || isBusy}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            fileInputRef.current?.click();
          }}
        >
          <Camera className="h-4 w-4" />
          <span className="ml-2">
            {isUploading ? t('UPLOADING') || 'Uploading...' : t('CHANGE_IMAGE') || 'Change Image'}
          </span>
        </Button>
      </div>
    </div>
  );
};
